import posthog from 'posthog-js'
import type { CapturedNetworkRequest } from 'posthog-js'
import type { Register, Store } from '../types'

// Expose PostHog on window for browser-console smoke tests after a deploy
// (`posthog.capture('test')`, `posthog.get_session_replay_url()`), matching
// the window.Bugsnag convention in bugsnag.ts. The npm import is
// module-scoped; only the snippet distribution attaches to window itself.
declare global {
  interface Window {
    posthog: typeof posthog
  }
}

// Replay ships request/response payloads to PostHog for debugging. Two
// data classes never survive capture:
//
// CREDENTIALS (hard line, everywhere) — three travel routes:
//   headers — Authorization bearer + cookies
//   URLs    — the legacy GET check-manager-access?pin=XXXX put the PIN in
//             the query (the POS now POSTs it; still scrubbed)
//   bodies  — login password/api_password, clerk pin, refresh_token /
//             clerk_token / session_token in auth traffic, gift_card_pin
//             inside otherwise-debuggable payment payloads
//
// CUSTOMER DATA — name + ids stay (the replication key: which customer
// to load to reproduce a bug), everything else goes:
//   /customers endpoints (search, profile, insights) are the PII
//     firehose, so they're ALLOWLIST-scrubbed: only id/name-shaped keys
//     survive and unknown future fields fail closed. Exception:
//     /customers/:id/springbig is exempt — loyalty offer/reward payloads
//     are needed to debug discount application, and the denylist below
//     still strips member contact fields from them.
//   Customer objects embedded elsewhere (cart state, receipts) keep
//     operational fields that drive checkout behavior (price_tier,
//     purchase limits, group_id) and lose PII by key: contact, birthdate,
//     medical, address, notes. Store address/phone fields are collateral
//     of the same keys; nothing debuggable lost.
const SECRET_KEY = /(^|_)(pin|password|pwd|secret|token|api_key)(_|$)/i
const SECRET_HEADER = /^(authorization|cookie|set-cookie)$/i
const AUTH_URL = /\/auth\//i
const CUSTOMER_URL = /\/customers/i
const CUSTOMER_URL_EXEMPT = /\/springbig/i
const KEEP_KEY = /(^|_)(id|ids|name|success|error)($|_)/i
// product_name / item_name match the generic name allowlist but reveal
// purchase history on /customers/insights — treat them as not-kept.
const KEEP_EXCLUDE = /(^|_)(product|item)(_|$)/i
const PII_KEY = /(^|_)(phone|mobile|email|birthdate|birthday|dob|medical|notes|address\d*|postal_code|city|province|licen[cs]e)($|_)/i

function isKeptKey(key: string): boolean {
  return KEEP_KEY.test(key) && !KEEP_EXCLUDE.test(key)
}

function scrubValues(node: unknown): unknown {
  if (Array.isArray(node)) return node.map(scrubValues)
  if (node !== null && typeof node === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(node)) {
      out[key] =
        SECRET_KEY.test(key) || PII_KEY.test(key) ? '[redacted]' : scrubValues(value)
    }
    return out
  }
  return node
}

// Allowlist walk for /customers payloads: structure and id/name keys
// survive, every other leaf is redacted — including keys that don't
// exist yet. Primitives inside an array have no key of their own, so
// they inherit the array's key (e.g. tags: ['vip'] redacts unless the
// array sits under a kept key).
function allowlistValues(node: unknown, underKeptKey = false): unknown {
  if (Array.isArray(node)) {
    return node.map((member) =>
      member !== null && typeof member === 'object'
        ? allowlistValues(member, underKeptKey)
        : underKeptKey
          ? member
          : '[redacted]',
    )
  }
  if (node !== null && typeof node === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(node)) {
      if (value !== null && typeof value === 'object') {
        out[key] = allowlistValues(value, isKeptKey(key))
      } else {
        out[key] = isKeptKey(key) ? value : '[redacted]'
      }
    }
    return out
  }
  return node
}

// Customer lookup sends the typed search term as ?q= — routinely a phone
// number, not just a name — and the on-screen input is already masked, so
// the URL must not ship what the video hides. Scoped to customer URLs:
// product search terms are non-PII and real debugging signal.
const SEARCH_PARAM = /^(q|search|query)$/i

// Captured names are usually absolute (PerformanceEntry), but the fetch/xhr
// capture path can surface the raw relative URL axios was called with.
function scrubUrl(url: string): string {
  if (!url.includes('?')) return url
  try {
    const parsed = new URL(url, window.location.origin)
    const isCustomerUrl = CUSTOMER_URL.test(url)
    let changed = false
    parsed.searchParams.forEach((_value, key) => {
      if (
        SECRET_KEY.test(key)
        || PII_KEY.test(key)
        || (isCustomerUrl && SEARCH_PARAM.test(key))
      ) {
        parsed.searchParams.set(key, 'redacted')
        changed = true
      }
    })
    return changed ? parsed.toString() : url
  } catch {
    return url
  }
}

function scrubBody(body: string | null | undefined, url: string): string | null | undefined {
  if (body == null || body === '') return body
  const isCustomerUrl = CUSTOMER_URL.test(url) && !CUSTOMER_URL_EXEMPT.test(url)
  try {
    const walker = isCustomerUrl ? allowlistValues : scrubValues
    return JSON.stringify(walker(JSON.parse(body)))
  } catch {
    // Not JSON. Every known secret/PII carrier is JSON, so pass non-JSON
    // through — except on auth and customer URLs, where an unparseable
    // payload must drop rather than ship unscrubbed.
    return AUTH_URL.test(url) || isCustomerUrl
      ? '[redacted: unparseable payload]'
      : body
  }
}

function maskNetworkRequest(data: CapturedNetworkRequest): CapturedNetworkRequest {
  for (const headers of [data.requestHeaders, data.responseHeaders]) {
    if (!headers) continue
    for (const key of Object.keys(headers)) {
      // Case varies by capture path (fetch lowercases, XHR preserves).
      if (SECRET_HEADER.test(key)) delete headers[key]
    }
  }
  data.name = scrubUrl(data.name)
  data.requestBody = scrubBody(data.requestBody, data.name)
  data.responseBody = scrubBody(data.responseBody, data.name)
  return data
}

interface PosIdentity {
  store: Store | null
  register: Register | null
}

interface InitPostHogOptions {
  getIdentity: () => PosIdentity
  subscribe: (listener: () => void) => void
}

let posthogInitialized = false

// Identity is keyed on the REGISTER, not the cashier: registers are the
// stable fleet unit support tickets reference ("Store X's front register
// was slow Tuesday"), and they survive logins/logouts within a shift.
// identify() is guarded so repeat store updates never re-merge persons;
// register() re-stamps the same values as super properties on every event.
function syncIdentity(getIdentity: InitPostHogOptions['getIdentity']) {
  const { store, register } = getIdentity()
  if (!register) return

  const distinctId = `register-${register.id}`
  const props = {
    store_id: register.store_id,
    store_name: store?.name ?? register.store_name,
    register_id: register.id,
    register_name: register.name,
    build_version: import.meta.env.VITE_BUILD_VERSION || 'dev',
  }
  if (posthog.get_distinct_id() !== distinctId) {
    posthog.identify(distinctId, props)
  }
  posthog.register(props)
}

export function initPostHog(options: InitPostHogOptions): boolean {
  if (posthogInitialized) return true

  const apiKey = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN?.trim()
  if (!apiKey) return false

  posthog.init(apiKey, {
    // DELIBERATELY HARDCODED — no env override. PostHog loads recorder/
    // config/surveys as <script> tags from the region's asset host
    // (us.i.posthog.com pairs with us-assets.i.posthog.com), and
    // vercel.json's script-src allows ONLY us-assets. An env knob here
    // could silently disagree with that CSP and kill replay (scripts
    // blocked, no recordings; boot-guard ignores third-party failures so
    // it degrades quietly — see the 2026-07-18 incident). Moving region
    // or proxying PostHog is a code change: update this constant AND
    // vercel.json script-src in the same commit.
    api_host: 'https://us.i.posthog.com',
    // '2025-05-24' defaults include history-change pageviews, which is what
    // makes TanStack Router SPA navigations show up as $pageview events.
    defaults: '2025-05-24',
    // Cookies are unreliable on the capacitor://localhost origin the iOS
    // webview serves from; localStorage works on every platform we ship.
    persistence: 'localStorage',
    enable_recording_console_log: true,
    session_recording: {
      // Inputs are masked wholesale (covers any typed credential); other
      // on-screen text records unmasked so replays stay debuggable. Class
      // "ph-mask" (rrweb's default maskTextClass) is available to redact
      // a specific element if one ever needs it.
      maskAllInputs: true,
      recordHeaders: true,
      recordBody: true,
      maskCapturedNetworkRequestFn: maskNetworkRequest,
    },
  })

  // Whether recording actually runs (and for which stores, at what sample
  // rate) is controlled server-side in the PostHog project's replay
  // settings — rollout/kill needs no app deploy.

  syncIdentity(options.getIdentity)
  options.subscribe(() => syncIdentity(options.getIdentity))

  // Attach the replay URL to every Bugsnag report so "buggy" tickets come
  // with footage. window.Bugsnag is only set once initBugsnag() has
  // started the client (main.tsx runs it before us), so this gate also
  // covers dev/preview builds with no Bugsnag key. The URL is computed at
  // error time — session ids rotate on idle/24h — and jumps playback to
  // 30s before the error.
  if (typeof window !== 'undefined' && window.Bugsnag) {
    window.Bugsnag.addOnError((event) => {
      event.addMetadata('posthog', {
        replay_url: posthog.get_session_replay_url({
          withTimestamp: true,
          timestampLookBack: 30,
        }),
      })
    })
  }

  if (typeof window !== 'undefined') {
    window.posthog = posthog
  }

  posthogInitialized = true
  return true
}

// Custom (non-autocapture) events. Replay only indexes what the app
// explicitly captures -- on-screen text and recorded network bodies are
// visible in a replay but not searchable -- so anything support needs to
// FIND a session by has to travel as an event property.
//
// Every caller so far sits on the checkout path, which must never fail
// because telemetry did: this no-ops before init (dev/preview builds ship
// without VITE_POSTHOG_PROJECT_TOKEN) and swallows whatever posthog-js
// throws, so call sites need no try/catch of their own. Store, register
// and build_version already ride along as super properties from
// syncIdentity -- don't re-send them per event.
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  if (!posthogInitialized) return
  try {
    posthog.capture(event, properties)
  } catch {
    // A dropped analytics event is never worth breaking a sale over.
  }
}
